// @ts-nocheck
import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HeroLayout1Data } from '../layout_1/hero-layout-1.component';

/**
 * Preview component for the "Hero Layout 2" template — renders a configurable hero section with image, title, body text and CTA handling.
 * @component
 * @selector app-hero-layout-2-preview
 * @example
 *   <app-hero-layout-2-preview
 *     [data]="sampleHeroData"
 *     [headingColor]="'#111'"
 *     [paragraphColor]="'#444'"
 *     [headingFont]="'Roboto'"
 *     [bodyFont]="'Inter'"
 *     [primaryColor]="'#007bff'"
 *     [titleStyles]="{ 'fontWeight': '700' }"
 *     (buttonClick)="onHeroButtonClick($event)">
 *   </app-hero-layout-2-preview>
 * @input {HeroLayout1Data | null} data - The hero content model (title, subtitle, image object with src/alt/scaletype, buttons, etc.) used to populate the preview. May be null for empty state.
 * @input {string | null} headingColor - CSS color value applied to the heading text.
 * @input {string | null} paragraphColor - CSS color value applied to the paragraph/body text.
 * @input {string | null} headingFont - Font family or descriptor applied to the heading text.
 * @input {string | null} bodyFont - Font family or descriptor applied to the body/paragraph text.
 * @input {string | null} primaryColor - Primary accent color used for buttons and highlights.
 * @input {any} titleStyles - Optional inline style object applied to the title element to override or augment default styles.
 * @output {EventEmitter<{link: string; event: Event}>} buttonClick - Emitted when a call-to-action button is clicked; payload contains the target link and the originating DOM Event.
 * @method onButtonClick(link: string | undefined, event: Event) - Click handler invoked by CTA elements; emits the buttonClick event only when a valid link is provided.
 * @method getImageSrc(): string - Resolves the image source to display, falling back from data.image.src to data.imageUrl to an empty string.
 * @method getImageAlt(): string - Returns the image alt text from the data model or a sensible default ("Car Wash") if none is provided.
 * @method getObjectFit(): string - Computes the appropriate CSS object-fit value based on the image scaletype (maps 'fill' → 'cover', 'fit' → 'contain', 'stretch' → 'fill').
 * @method getImageStyles(): any - Returns a style object for the image element (currently sets object-fit based on getObjectFit and relies on CSS for sizing).
 * @notes
 *   - This is a standalone, presentational Angular component that imports CommonModule and uses an external template and styles.
 *   - No explicit Angular lifecycle hooks (ngOnInit, ngOnChanges, etc.) are required or implemented; the component reacts to input changes via bindings.
 */
@Component({
  selector: 'app-hero-layout-2-preview',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './hero-layout-2.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./hero-layout-2.component.scss'],
})
export class HeroLayout2PreviewComponent {
  @Input() data: HeroLayout1Data | null = null;
  @Input() headingColor: string | null = null;
  @Input() paragraphColor: string | null = null;
  @Input() headingFont: string | null = null;
  @Input() bodyFont: string | null = null;
  @Input() primaryColor: string | null = null;
  @Input() titleStyles: any = {};
  @Output() buttonClick = new EventEmitter<{ link: string; event: Event }>();

  onButtonClick(link: string | undefined, event: Event): void {
    if (link) {
      this.buttonClick.emit({ link, event });
    }
  }

  getImageSrc(): string {
    return this.data?.image?.src || this.data?.imageUrl || '';
  }

  getImageAlt(): string {
    return this.data?.image?.alt || 'Car Wash';
  }

  /**
   * Return a CSS object-fit value based on this.data.image.scaletype ('fill'→'cover', 'fit'→'contain', 'stretch'→'fill').
   * @example
   * etObjectFit()
   * 'contain'
   * @returns {string} CSS object-fit value corresponding to the image scale type.
   */
  getObjectFit(): string {
    const scaletype = this.data?.image?.scaletype || 'fit';
    // Map scale types to CSS object-fit values
    switch (scaletype) {
      case 'fill':
        return 'cover'; // Fill: fills container, maintains aspect ratio, may crop
      case 'fit':
        return 'contain'; // Fit: fits within container, maintains aspect ratio, no cropping
      case 'stretch':
        return 'fill'; // Stretch: stretches to fill container exactly, may distort
      default:
        return 'contain';
    }
  }

  /**
   * Compute inline styles for the hero panel image; returns an object with CSS properties that ensure the image fills its container and applies the computed object-fit value (reads this.data?.image?.scaletype with a default of 'fit' and delegates to this.getObjectFit()).
   * @example
   * etImageStyles()
   * { 'object-fit': 'contain' }
   * @returns {any} Return an object containing CSS style properties for the image element.
   **/
  getImageStyles(): any {
    const baseStyles: any = {
      'object-fit': this.getObjectFit(),
    };

    // For all types, the image should fill its container
    // The CSS already sets width: 100% and height: 100% on .car-illustration
    // The difference is in object-fit behavior

    return baseStyles;
  }
}
